import React, { useRef, createElement } from 'react';
import { DownloadIcon } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toPng } from 'html-to-image';
import DashboardLayout from '../../components/layout/DashboardLayout';
// Helper function to calculate CRC16-CCITT
const calculateCRC = (data: string): string => {
  let crc = 0xffff;
  for (let i = 0; i < data.length; i++) {
    crc ^= data.charCodeAt(i) << 8;
    for (let j = 0; j < 8; j++) {
      if (crc & 0x8000) {
        crc = crc << 1 ^ 0x1021;
      } else {
        crc = crc << 1;
      }
    }
  }
  return (crc & 0xffff).toString(16).toUpperCase().padStart(4, '0');
};
// Helper function to format EMV tag-length-value
const formatTLV = (tag: string, value: string): string => {
  const length = value.length.toString().padStart(2, '0');
  return `${tag}${length}${value}`;
};
// Generate EMV Co. compliant QR code string
const generateEMVQRString = (merchantData: any): string => {
  let qrString = '';
  // Tag 00: Payload Format Indicator
  qrString += formatTLV('00', '01');
  // Tag 01: Point of Initiation Method (12 = Static)
  qrString += formatTLV('01', '12');
  // Tag 26-51: Merchant Account Information (using tag 26 for primary account)
  const merchantAccountInfo = formatTLV('00', 'PK') +
  // Country
  formatTLV('01', merchantData.mid) +
  // Merchant ID
  formatTLV('02', merchantData.tid); // Terminal ID
  qrString += formatTLV('26', merchantAccountInfo);
  // Tag 52: Merchant Category Code
  qrString += formatTLV('52', '5204');
  // Tag 53: Transaction Currency (586 = PKR)
  qrString += formatTLV('53', '586');
  // Tag 58: Country Code
  qrString += formatTLV('58', 'PK');
  // Tag 59: Merchant Name
  qrString += formatTLV('59', merchantData.name);
  // Tag 60: Merchant City
  qrString += formatTLV('60', 'Karachi');
  // Tag 63: CRC (placeholder, will be calculated)
  qrString += '6304';
  // Calculate and append CRC
  const crc = calculateCRC(qrString);
  qrString += crc;
  return qrString;
};
const StaticQRCPage = () => {
  const qrCodeRef = useRef<HTMLDivElement>(null);
  const merchantData = {
    name: 'AQS Store',
    mid: '050102100023650',
    tid: '2365010001'
  };
  const qrCodeString = generateEMVQRString(merchantData);
  const handleDownload = () => {
    if (qrCodeRef.current) {
      toPng(qrCodeRef.current, {
        quality: 1,
        pixelRatio: 2,
        backgroundColor: '#ffffff'
      }).then(dataUrl => {
        const link = document.createElement('a');
        link.download = `QR-Static-${merchantData.mid}.png`;
        link.href = dataUrl;
        link.click();
      }).catch(err => {
        console.error('Error downloading QR code:', err);
      });
    }
  };
  return <DashboardLayout data-id="element-1706">
      <div className="mb-6" data-id="element-1707">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1708">Static QR Code</h1>
        <p className="text-gray-600" data-id="element-1709">
          Generate a static QR code without amount
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1710">
        <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-1711">
          <h2 className="text-lg font-semibold mb-4" data-id="element-1712">Merchant Information</h2>
          <div className="space-y-4" data-id="element-1713">
            <div data-id="element-1714">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1715">
                Merchant Name
              </label>
              <div className="p-2 bg-gray-50 rounded border border-gray-200" data-id="element-1716">
                {merchantData.name}
              </div>
            </div>
            <div data-id="element-1717">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1718">
                Merchant ID
              </label>
              <div className="p-2 bg-gray-50 rounded border border-gray-200" data-id="element-1719">
                {merchantData.mid}
              </div>
            </div>
            <div data-id="element-1720">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1721">
                Till ID
              </label>
              <div className="p-2 bg-gray-50 rounded border border-gray-200" data-id="element-1722">
                {merchantData.tid}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6 flex flex-col items-center justify-center" data-id="element-1723">
          <div ref={qrCodeRef} className="bg-white p-6 flex flex-col items-center" data-id="element-1724">
            <div className="mb-4 p-4 bg-white border-2 border-gray-300 rounded-lg inline-block" data-id="element-1725">
              <QRCodeSVG id="qr-code-canvas" value={qrCodeString} size={256} level="M" includeMargin={true} fgColor="#000000" bgColor="#FFFFFF" data-id="element-1726" />
            </div>
            <p className="text-center text-gray-700 mb-6" data-id="element-1727">
              Scan this QR code to make a payment
            </p>
            <div className="flex justify-center items-center space-x-4" data-id="element-1728">
              <img src="https://cdn.magicpatterns.com/uploads/uyg5nASZDGjrCe2rJaNmzm/Visa_Logo.png" alt="Visa" className="h-8 object-contain" data-id="element-1729" />
              <img src="https://cdn.magicpatterns.com/uploads/wTaFUzAaMXmqM9o6YPo8t5/MasterCard_Logo.png" alt="MasterCard" className="h-8 object-contain" data-id="element-1730" />
              <img src="https://cdn.magicpatterns.com/uploads/8qxbwmnN4pa5FLphCCtDLR/UnionPay_Logo.png" alt="UnionPay" className="h-8 object-contain" data-id="element-1731" />
              <img src="https://cdn.magicpatterns.com/uploads/vGk29PVKWf7oAsHTkLwRwU/PayPak_Logo.png" alt="PayPak" className="h-8 object-contain" data-id="element-1732" />
              <img src="https://cdn.magicpatterns.com/uploads/dW1dUF5BFKkWuGeGttfoz6/RAAST_Logo.png" alt="RAAST" className="h-8 object-contain" data-id="element-1733" />
            </div>
          </div>
          <div className="mt-6" data-id="element-1734">
            <button onClick={handleDownload} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center" data-id="element-1735">
              <DownloadIcon className="h-4 w-4 mr-2" data-id="element-1736" />
              Download QR Code
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>;
};
export default StaticQRCPage;